import React, { useState, useEffect } from 'react';
import { paymentAccountAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';

// ── Config ────────────────────────────────────────────────────────────────────
// NOTE: Upwork is intentionally excluded from auto-routing — account is chosen per contract
const PLATFORMS = {
  upwork_personal: { label:'Upwork (Personal)',    icon:'🟢', color:'#14a800', bg:'#e6f9e6' },
  upwork_company:  { label:'Upwork (Company)',     icon:'🏢', color:'#0d7a00', bg:'#d4f5d4' },
  wise:            { label:'Wise',                 icon:'💙', color:'#163300', bg:'#c6f135' },
  paypal:          { label:'PayPal',               icon:'💳', color:'#003087', bg:'#e8f0fe' },
  local_gst:       { label:'Local Client (GST)',   icon:'🧾', color:'#d97706', bg:'#fef3c7' },
  local_nogst:     { label:'Local (No GST)',       icon:'📋', color:'var(--pm-primary)', bg:'#ede9fe' },
  bank_transfer:   { label:'Bank Transfer',        icon:'🏦', color:'#0f766e', bg:'#ccfbf1' },
  razorpay:        { label:'Razorpay',             icon:'💎', color:'#2d6be4', bg:'#e8f0fe' },
  other:           { label:'Other',                icon:'📎', color:'#64748b', bg:'#f1f5f9' },
};
const ALL_PLATFORMS = { ...PLATFORMS };

const ACCOUNT_TYPES = [
  { v:'bank_india', l:'Indian Bank Account', icon:'🏦' },
  { v:'paypal',     l:'PayPal Account',      icon:'💳' },
  { v:'wise',       l:'Wise Account',        icon:'💙' },
  { v:'upwork',     l:'Upwork Profile',      icon:'🟢' },
  { v:'upi',        l:'UPI',                 icon:'📱' },
  { v:'other',      l:'Other',               icon:'📎' },
];

const OWNER_TYPES = [
  { v:'self',    l:'Your Account',     icon:'👤' },
  { v:'spouse',  l:'Spouse Account',   icon:'💑' },
  { v:'company', l:'Company Account',  icon:'🏢' },
  { v:'other',   l:'Other',            icon:'📎' },
];

const OWNER_COLORS = { self:'var(--pm-primary)', spouse:'#ec4899', company:'#0f766e', other:'#64748b' };
const OWNER_BG     = { self:'#ede9fe', spouse:'#fce7f3', company:'#ccfbf1', other:'#f1f5f9' };

const INP  = (extra={}) => ({ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:"'Outfit',sans-serif", background:'#fafbfc', transition:'border .15s', ...extra });
const LABEL = { display:'block', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:5 };
const CARD  = { background:'#fff', borderRadius:16, boxShadow:'0 2px 16px #00000009', border:'1px solid #f0f4f8' };
const BTN   = (v='default') => ({ padding:'9px 18px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7, transition:'all .15s',
  ...(v==='primary' ? { background:'linear-gradient(135deg,#0f172a,#334155)', color:'#fff', boxShadow:'0 4px 12px #0f172a22' }
    : v==='danger'  ? { background:'#fee2e2', color:'#dc2626' }
    : v==='ghost'   ? { background:'transparent', color:'#64748b', border:'1.5px solid #e2e8f0' }
    :                 { background:'#f8fafc', color:'#475569', border:'1.5px solid #e2e8f0' }) });

const EMPTY = {
  name:'', owner:'', ownerType:'self', type:'bank_india',
  defaultForPlatforms:[],
  bankName:'', accountHolder:'', accountNumber:'', ifscCode:'', branchName:'', accountType:'savings', upiId:'',
  gstNumber:'', panNumber:'', isGSTRegistered:false,
  paypalEmail:'', wiseEmail:'', wiseAccountId:'', wiseCurrency:'USD',
  upworkProfileUrl:'', upworkProfileType:'personal', upworkWithdrawTo:'Wise',
  primaryCurrency:'INR', notes:'',
};

export default function PaymentAccounts() {
  const confirm = useConfirm();
  const [accounts,   setAccounts]   = useState([]);
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(EMPTY);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const r = await paymentAccountAPI.list(); setAccounts(r.data.accounts||[]); }
    catch { toast.error('Failed to load accounts'); }
    setLoading(false);
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (acc) => {
    setForm({ ...EMPTY, ...acc });
    setEditId(acc._id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Account name required');
    if (!form.owner.trim()) return toast.error('Owner name required');
    try {
      if (editId) { await paymentAccountAPI.update(editId, form); toast.success('Account updated!'); }
      else        { await paymentAccountAPI.create(form);         toast.success('Account added!'); }
      setShowForm(false);
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to save'); }
  };

  const remove = async (id) => {
    const ok = await confirm('Remove this account?', { title: 'Remove Account?', variant: 'danger' });
    if (!ok) return;
    try { await paymentAccountAPI.remove(id); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const togglePlatform = (plat) => {
    setForm(f => ({
      ...f,
      defaultForPlatforms: f.defaultForPlatforms.includes(plat)
        ? f.defaultForPlatforms.filter(p => p !== plat)
        : [...f.defaultForPlatforms, plat]
    }));
  };

  // Group accounts by owner type
  const grouped = accounts.reduce((g, acc) => {
    const k = acc.ownerType || 'self';
    if (!g[k]) g[k] = [];
    g[k].push(acc);
    return g;
  }, {});

  return (
    <div style={{ maxWidth:1100, margin:'0 auto', fontFamily:"'Outfit',sans-serif", paddingBottom:60 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(30px)} to{opacity:1;transform:translateX(0)} }
        input:focus,select:focus,textarea:focus { border-color:#0f172a!important; background:#fff!important; outline:none; }
        .acc-card:hover { box-shadow:0 8px 32px #0000001a!important; transform:translateY(-2px); }
        .plat-chip { transition:all .15s; cursor:pointer; user-select:none; }
        .plat-chip:hover { opacity:.85; }
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:30, fontWeight:900, color:'#0f172a', letterSpacing:'-0.02em' }}>💳 Payment Accounts</h1>
          <p style={{ margin:'5px 0 0', color:'#94a3b8', fontSize:14 }}>Manage where money lands — personal, spouse, and company accounts</p>
        </div>
        <button style={BTN('primary')} onClick={openNew}>+ Add Account</button>
      </div>

      {/* Platform → Account mapping summary */}
      {accounts.length > 0 && (
        <div style={{ ...CARD, padding:'18px 22px', marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
              <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em' }}>Platform → Account Routing</div>
              <div style={{ padding:'4px 12px', borderRadius:20, background:'#fef3c7', color:'#92400e', fontSize:11, fontWeight:700 }}>🟢 Upwork = selected per contract</div>
            </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:10 }}>
            {Object.entries(ALL_PLATFORMS).map(([platKey, platCfg]) => {
              const acc = accounts.find(a => a.defaultForPlatforms?.includes(platKey));
              return (
                <div key={platKey} style={{ padding:'10px 14px', borderRadius:11, background: acc ? platCfg.bg : '#f8fafc', border:`1.5px solid ${acc ? platCfg.color+'40' : '#e2e8f0'}`, display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:18 }}>{platCfg.icon}</span>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:700, color: acc ? platCfg.color : '#94a3b8' }}>{platCfg.label}</div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#0f172a', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {acc ? `→ ${acc.name}` : <span style={{ color:'#cbd5e1' }}>Not assigned</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Accounts grouped by owner */}
      {loading ? (
        <div style={{ textAlign:'center', padding:80 }}>
          <div style={{ width:36, height:36, border:'3px solid #f1f5f9', borderTop:'3px solid #0f172a', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto' }}/>
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ ...CARD, textAlign:'center', padding:'60px 20px' }}>
          <div style={{ fontSize:52, marginBottom:14 }}>💳</div>
          <div style={{ fontWeight:700, fontSize:18, color:'#475569', marginBottom:8 }}>No accounts yet</div>
          <div style={{ fontSize:13, color:'#94a3b8', marginBottom:24, maxWidth:400, margin:'0 auto 24px' }}>Add your bank accounts, PayPal, Wise, and Upwork profiles so invoices automatically route to the right account</div>
          <button style={BTN('primary')} onClick={openNew}>+ Add First Account</button>
        </div>
      ) : (
        ['self','spouse','company','other'].map(ownerType => {
          const group = grouped[ownerType];
          if (!group?.length) return null;
          const ownerCfg = OWNER_TYPES.find(o=>o.v===ownerType);
          return (
            <div key={ownerType} style={{ marginBottom:28, animation:'fadeUp .3s ease' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <span style={{ fontSize:20 }}>{ownerCfg?.icon}</span>
                <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#0f172a' }}>{ownerCfg?.l}</h2>
                <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:OWNER_BG[ownerType], color:OWNER_COLORS[ownerType] }}>{group.length}</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
                {group.map(acc => {
                  const typeCfg = ACCOUNT_TYPES.find(t=>t.v===acc.type);
                  return (
                    <div key={acc._id} className="acc-card" style={{ ...CARD, padding:'20px 22px', transition:'all .2s', cursor:'default', borderLeft:`4px solid ${OWNER_COLORS[acc.ownerType]||'#e2e8f0'}` }}>
                      {/* Card header */}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                        <div>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <span style={{ fontSize:20 }}>{typeCfg?.icon}</span>
                            <div style={{ fontWeight:800, fontSize:15, color:'#0f172a' }}>{acc.name}</div>
                          </div>
                          <div style={{ fontSize:12, color:'#64748b' }}>{acc.owner}</div>
                        </div>
                        <div style={{ display:'flex', gap:6 }}>
                          <button style={{ ...BTN(), padding:'5px 10px', fontSize:12 }} onClick={()=>openEdit(acc)}>✏️</button>
                          <button style={{ ...BTN('danger'), padding:'5px 10px', fontSize:12 }} onClick={()=>remove(acc._id)}>🗑</button>
                        </div>
                      </div>

                      {/* Account details */}
                      <div style={{ background:'#f8fafc', borderRadius:10, padding:'12px 14px', marginBottom:12, fontSize:12 }}>
                        {acc.type === 'bank_india' && <>
                          {acc.bankName && <div style={{ fontWeight:700, color:'#0f172a', marginBottom:4 }}>{acc.bankName}</div>}
                          {acc.accountHolder && <div style={{ color:'#64748b' }}>Holder: {acc.accountHolder}</div>}
                          {acc.accountNumber && <div style={{ fontFamily:'monospace', color:'#475569', marginTop:2 }}>A/C: {acc.accountNumber}</div>}
                          {acc.ifscCode && <div style={{ color:'#64748b' }}>IFSC: {acc.ifscCode}</div>}
                          {acc.upiId && (
                            <div style={{ marginTop:6, display:'inline-flex', alignItems:'center', gap:6, background:'#ede9fe', borderRadius:8, padding:'4px 10px' }}>
                              <span>📱</span>
                              <span style={{ fontWeight:700, color:'#5b21b6', fontFamily:'monospace', fontSize:12 }}>{acc.upiId}</span>
                            </div>
                          )}
                          {acc.isGSTRegistered && <div style={{ marginTop:6, display:'inline-block', background:'#fef3c7', color:'#d97706', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700 }}>GST: {acc.gstNumber}</div>}
                        </>}
                        {acc.type === 'paypal' && <div style={{ color:'#003087', fontWeight:600 }}>📧 {acc.paypalEmail}</div>}
                        {acc.type === 'wise' && <>
                          <div style={{ fontWeight:700, color:'#163300' }}>💙 Wise</div>
                          {acc.wiseEmail && <div style={{ color:'#64748b', marginTop:3 }}>{acc.wiseEmail}</div>}
                          <div style={{ color:'#94a3b8', marginTop:2 }}>Currency: {acc.wiseCurrency}</div>
                        </>}
                        {acc.type === 'upwork' && <>
                          <div style={{ fontWeight:700, color:'#14a800' }}>🟢 Upwork — {acc.upworkProfileType === 'company' ? 'Company Profile' : 'Personal Profile'}</div>
                          {acc.upworkWithdrawTo && <div style={{ color:'#64748b', marginTop:3 }}>Withdraws to: {acc.upworkWithdrawTo}</div>}
                        </>}
                        {acc.type === 'upi' && <div style={{ color:'var(--pm-primary)', fontWeight:600 }}>📱 {acc.upiId}</div>}
                      </div>

                      {/* Balance summary */}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background: acc.totalReceived > 0 ? '#f0fdf4' : '#f8fafc', borderRadius:10, padding:'10px 14px', marginBottom:12, border:`1px solid ${acc.totalReceived > 0 ? '#bbf7d0' : '#e2e8f0'}` }}>
                        <div>
                          <div style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Total Received</div>
                          <div style={{ fontSize:18, fontWeight:900, color: acc.totalReceived > 0 ? '#16a34a' : '#cbd5e1', marginTop:2 }}>
                            {acc.totalReceived > 0
                              ? new Intl.NumberFormat('en-US', { style:'currency', currency: acc.primaryCurrency || 'INR', maximumFractionDigits:2 }).format(acc.totalReceived)
                              : '—'
                            }
                          </div>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Paid Invoices</div>
                          <div style={{ fontSize:18, fontWeight:900, color: acc.invoiceCount > 0 ? '#0f172a' : '#cbd5e1', marginTop:2 }}>{acc.invoiceCount || '—'}</div>
                        </div>
                      </div>

                      {/* Platform routing chips */}
                      {acc.defaultForPlatforms?.length > 0 && (
                        <div>
                          <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Auto-selected for</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                            {acc.defaultForPlatforms.map(p => {
                              const pc = ALL_PLATFORMS[p];
                              return <span key={p} style={{ padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700, background:pc?.bg, color:pc?.color }}>{pc?.icon} {pc?.label}</span>;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {/* ──── FORM MODAL ──────────────────────────────────────────────────── */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, overflowY:'auto', padding:'32px 16px', backdropFilter:'blur(4px)' }}
          onClick={()=>setShowForm(false)}>
          <div style={{ background:'#fff', borderRadius:22, padding:32, width:640, maxWidth:'100%', boxShadow:'0 32px 100px #0003', animation:'slideIn .2s ease', margin:'auto' }}
            onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 22px', fontSize:20, fontWeight:900, color:'#0f172a' }}>{editId ? '✏️ Edit Account' : '➕ Add Payment Account'}</h3>

            {/* Account type + Owner */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:18 }}>
              <div>
                <label style={LABEL}>Account Type *</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                  {ACCOUNT_TYPES.map(t => (
                    <button key={t.v} type="button" onClick={()=>setForm(f=>({...f,type:t.v}))}
                      style={{ padding:'9px 10px', borderRadius:9, border:`2px solid ${form.type===t.v?'#0f172a':'#e2e8f0'}`, background:form.type===t.v?'#0f172a':'#fafbfc',
                        color:form.type===t.v?'#fff':'#475569', cursor:'pointer', fontSize:12, fontWeight:700, textAlign:'left', display:'flex', gap:6, alignItems:'center' }}>
                      <span>{t.icon}</span>{t.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={LABEL}>Account Owner *</label>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {OWNER_TYPES.map(o => (
                    <button key={o.v} type="button" onClick={()=>setForm(f=>({...f,ownerType:o.v}))}
                      style={{ padding:'9px 12px', borderRadius:9, border:`2px solid ${form.ownerType===o.v?OWNER_COLORS[o.v]:'#e2e8f0'}`,
                        background:form.ownerType===o.v?OWNER_BG[o.v]:'#fafbfc',
                        color:form.ownerType===o.v?OWNER_COLORS[o.v]:'#475569', cursor:'pointer', fontSize:13, fontWeight:700, textAlign:'left', display:'flex', gap:8, alignItems:'center' }}>
                      <span>{o.icon}</span>{o.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Name + Owner name */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
              <div>
                <label style={LABEL}>Account Label * <span style={{ fontWeight:400 }}>(shown in invoices)</span></label>
                <input style={INP()} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Amritpal - HDFC Personal"/>
              </div>
              <div>
                <label style={LABEL}>Full Owner Name *</label>
                <input style={INP()} value={form.owner} onChange={e=>setForm(f=>({...f,owner:e.target.value}))} placeholder="e.g. Amritpal Singh"/>
              </div>
            </div>

            {/* ── Type-specific fields ── */}
            {form.type === 'bank_india' && (
              <div style={{ background:'#f8fafc', borderRadius:12, padding:16, marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>Bank Details</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div><label style={LABEL}>Bank Name</label><input style={INP()} value={form.bankName} onChange={e=>setForm(f=>({...f,bankName:e.target.value}))} placeholder="HDFC Bank / SBI / ICICI"/></div>
                  <div><label style={LABEL}>Account Holder</label><input style={INP()} value={form.accountHolder} onChange={e=>setForm(f=>({...f,accountHolder:e.target.value}))} placeholder="Full name on account"/></div>
                  <div><label style={LABEL}>Account Number</label><input style={INP()} value={form.accountNumber} onChange={e=>setForm(f=>({...f,accountNumber:e.target.value}))} placeholder="0001234567890"/></div>
                  <div><label style={LABEL}>IFSC Code</label><input style={INP()} value={form.ifscCode} onChange={e=>setForm(f=>({...f,ifscCode:e.target.value}))} placeholder="HDFC0001234"/></div>
                  <div><label style={LABEL}>Branch</label><input style={INP()} value={form.branchName} onChange={e=>setForm(f=>({...f,branchName:e.target.value}))} placeholder="Ludhiana Main"/></div>
                  <div>
                    <label style={LABEL}>Account Type</label>
                    <select style={INP()} value={form.accountType} onChange={e=>setForm(f=>({...f,accountType:e.target.value}))}>
                      <option value="savings">Savings</option>
                      <option value="current">Current</option>
                    </select>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={LABEL}>UPI ID (optional)</label>
                    <input style={INP()} value={form.upiId} onChange={e=>setForm(f=>({...f,upiId:e.target.value}))} placeholder="amritpal@hdfc or phone@upi"/>
                  </div>
                </div>
                <div style={{ marginTop:12 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:9, cursor:'pointer', fontSize:13, fontWeight:600, color:'#475569' }}>
                    <input type="checkbox" checked={form.isGSTRegistered} onChange={e=>setForm(f=>({...f,isGSTRegistered:e.target.checked}))} style={{ width:16, height:16 }}/>
                    This account is GST registered
                  </label>
                  {form.isGSTRegistered && (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10 }}>
                      <div><label style={LABEL}>GST Number</label><input style={INP()} value={form.gstNumber} onChange={e=>setForm(f=>({...f,gstNumber:e.target.value}))} placeholder="22AAAAA0000A1Z5"/></div>
                      <div><label style={LABEL}>PAN Number</label><input style={INP()} value={form.panNumber} onChange={e=>setForm(f=>({...f,panNumber:e.target.value}))} placeholder="AAAAA0000A"/></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {form.type === 'paypal' && (
              <div style={{ background:'#e8f0fe', borderRadius:12, padding:16, marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#003087', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>💳 PayPal Details</div>
                <div><label style={LABEL}>PayPal Email</label><input style={INP()} value={form.paypalEmail} onChange={e=>setForm(f=>({...f,paypalEmail:e.target.value}))} placeholder="your@paypal.com"/></div>
              </div>
            )}

            {form.type === 'wise' && (
              <div style={{ background:'#d4f5e0', borderRadius:12, padding:16, marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#163300', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>💙 Wise Details</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div><label style={LABEL}>Wise Email</label><input style={INP()} value={form.wiseEmail} onChange={e=>setForm(f=>({...f,wiseEmail:e.target.value}))} placeholder="wise@email.com"/></div>
                  <div>
                    <label style={LABEL}>Primary Receive Currency</label>
                    <select style={INP()} value={form.wiseCurrency} onChange={e=>setForm(f=>({...f,wiseCurrency:e.target.value}))}>
                      {['USD','GBP','EUR','AUD','CAD','SGD'].map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div><label style={LABEL}>Wise Account ID (optional)</label><input style={INP()} value={form.wiseAccountId} onChange={e=>setForm(f=>({...f,wiseAccountId:e.target.value}))} placeholder="Optional Wise account ID"/></div>
                </div>
              </div>
            )}

            {form.type === 'upwork' && (
              <div style={{ background:'#e6f9e6', borderRadius:12, padding:16, marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#14a800', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>🟢 Upwork Profile</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={LABEL}>Profile Type</label>
                    <select style={INP()} value={form.upworkProfileType} onChange={e=>setForm(f=>({...f,upworkProfileType:e.target.value}))}>
                      <option value="personal">Personal Profile</option>
                      <option value="company">Company Profile</option>
                    </select>
                  </div>
                  <div>
                    <label style={LABEL}>Withdrawals Go To</label>
                    <select style={INP()} value={form.upworkWithdrawTo} onChange={e=>setForm(f=>({...f,upworkWithdrawTo:e.target.value}))}>
                      <option value="Wise">Wise</option>
                      <option value="Direct Bank">Direct Bank Transfer</option>
                      <option value="PayPal">PayPal</option>
                    </select>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={LABEL}>Upwork Profile URL (optional)</label>
                    <input style={INP()} value={form.upworkProfileUrl} onChange={e=>setForm(f=>({...f,upworkProfileUrl:e.target.value}))} placeholder="https://upwork.com/freelancers/~..."/>
                  </div>
                </div>
              </div>
            )}

            {form.type === 'upi' && (
              <div style={{ background:'#ede9fe', borderRadius:12, padding:16, marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#5b21b6', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>📱 UPI Details</div>
                <div><label style={LABEL}>UPI ID</label><input style={INP()} value={form.upiId} onChange={e=>setForm(f=>({...f,upiId:e.target.value}))} placeholder="name@hdfc or +91XXXXXXXXXX@upi"/></div>
              </div>
            )}

            {/* Platform routing */}
            <div style={{ marginBottom:18 }}>
              <label style={{ ...LABEL, marginBottom:10 }}>Auto-select this account for these platforms</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:7 }}>
                {Object.entries(PLATFORMS).map(([k, cfg]) => {
                  const active = form.defaultForPlatforms.includes(k);
                  return (
                    <div key={k} className="plat-chip" onClick={()=>togglePlatform(k)}
                      style={{ padding:'8px 12px', borderRadius:10, border:`2px solid ${active?cfg.color:'#e2e8f0'}`,
                        background:active?cfg.bg:'#fafbfc', display:'flex', alignItems:'center', gap:7 }}>
                      <span style={{ fontSize:16 }}>{cfg.icon}</span>
                      <div>
                        <div style={{ fontSize:12, fontWeight:700, color:active?cfg.color:'#475569' }}>{cfg.label}</div>
                      </div>
                      {active && <span style={{ marginLeft:'auto', fontSize:14, color:cfg.color }}>✓</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:7 }}>When creating an invoice for these platforms, this account will be pre-selected automatically.</div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom:20 }}>
              <label style={LABEL}>Notes (internal)</label>
              <textarea style={{ ...INP(), height:60, resize:'vertical' }} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Any additional notes about this account..."/>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button style={BTN('ghost')} onClick={()=>setShowForm(false)}>Cancel</button>
              <button style={BTN('primary')} onClick={save}>{editId?'💾 Update Account':'✓ Add Account'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
